import React, { useState, useContext, useEffect } from "react";
import {
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  makeStyles,
  Popover,
  Grid,
  Fade,
  Tooltip
} from "@material-ui/core";
import EmojiPicker from 'emoji-picker-react';
import MessageActionButton from "../MessageActionButton";
import AnimatedReactionPicker from "../AnimatedReactionPicker";
import {
  Reply as ReplyIcon,
  Forward as ForwardIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  EmojiEmotions as EmojiIcon,
  Person as PersonIcon,
  RecordVoiceOver as TranscribeIcon,
  MoreVert as MoreIcon,
  Star as StarIcon,
  FileCopy as CopyIcon
} from "@material-ui/icons";
import { GetApp as DownloadIcon } from "@material-ui/icons";
import AddCircleOutlineIcon from "@mui/icons-material/Add";
import { useHistory } from "react-router-dom";

import { EditMessageContext } from "../../context/EditingMessage/EditingMessageContext";
import { ForwardMessageContext } from "../../context/ForwarMessage/ForwardMessageContext";
import { ReplyMessageContext } from "../../context/ReplyingMessage/ReplyingMessageContext";
import { AuthContext } from "../../context/Auth/AuthContext";
import { TicketsContext } from "../../context/Tickets/TicketsContext";
import AcceptTicketWithoutQueue from "../AcceptTicketWithoutQueueModal";
import ShowTicketOpen from "../ShowTicketOpenModal";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import { toast } from "react-toastify";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  // � Keyframes para animações mais fluidas
  "@keyframes slideInMenu": {
    "0%": {
      opacity: 0,
      transform: "translateY(-10px) scale(0.95)",
    },
    "100%": {
      opacity: 1,
      transform: "translateY(0) scale(1)",
    },
  },
  
  "@keyframes bounceIcon": {
    "0%, 100%": {
      transform: "scale(1.1)",
    },
    "50%": {
      transform: "scale(1.2)",
    },
  },
  
  "@keyframes pulseGlow": {
    "0%, 100%": {
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.12)",
    },
    "50%": {
      boxShadow: "0 4px 16px rgba(0, 168, 132, 0.3)",
    },
  },

  // �🎯 Container principal dos botões flutuantes - melhorado
  floatingActionsContainer: {
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    zIndex: 1000,
    display: "flex",
    gap: "6px", // Aumentado para melhor espaçamento
    opacity: 0,
    visibility: "hidden",
    transition: "all 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55)", // Bounce mais suave
    
    // Posicionamento baseado na direção da mensagem
    "&.left": {
      right: "-70px", // Mais afastado para melhor visual
    },
    
    "&.right": {
      left: "-70px",
    },
    
    "&.visible": {
      opacity: 1,
      visibility: "visible",
      transform: "translateY(-50%) scale(1)",
    }
  },
  
  // 🎯 Botões flutuantes individuais - estilo WhatsApp
  floatingButton: {
    width: "32px",
    height: "32px",
    backgroundColor: "rgba(255, 255, 255, 0.95)",
    backdropFilter: "blur(10px)",
    border: "1px solid rgba(0, 0, 0, 0.08)",
    borderRadius: "50%",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.12)",
    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
    color: "#54656f",
    
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 1)",
      transform: "scale(1.05)",
      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.18)",
      color: "#111b21"
    },
    
    "& .MuiSvgIcon-root": {
      fontSize: "18px"
    },
    
    "&.primary": {
      backgroundColor: "#00a884",
      color: "white",
      
      "&:hover": {
        backgroundColor: "#008c6f",
        color: "white"
      }
    }
  },
    // 🎯 Menu moderno - estilo WhatsApp Web melhorado
  modernMenu: {
    "& .MuiPaper-root": {
      borderRadius: "12px", // Mais arredondado
      boxShadow: "0 8px 32px rgba(11, 20, 26, 0.2)", // Sombra mais suave
      border: "1px solid rgba(0, 0, 0, 0.05)",
      minWidth: "180px", // Ligeiramente maior
      maxWidth: "220px",
      backgroundColor: "#ffffff",
      overflow: "visible",
      animation: "$slideInMenu 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55)",
      
      // Backdrop blur para modernidade
      backdropFilter: "blur(10px)",
      background: "rgba(255, 255, 255, 0.95)",
    }
  },    // 🎯 Itens do menu - estilo WhatsApp com hover fill melhorado
  menuItem: {
    padding: "14px 18px", // Padding mais generoso
    fontSize: "14px",
    color: "#3b4a54",
    minHeight: "52px", // Altura maior para melhor toque
    position: "relative",
    overflow: "hidden",
    borderRadius: "0px",
    transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
    display: "flex",
    alignItems: "center",
    cursor: "pointer",
    backgroundColor: "transparent",
    
    // Fundo que preenche da esquerda para direita - melhorado
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      width: "0%",
      height: "100%",
      background: "linear-gradient(90deg, rgba(240, 242, 245, 0.4) 0%, rgba(240, 242, 245, 0.8) 100%)", // Gradiente sutil
      transition: "width 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
      zIndex: 0,
    },
    
    // Efeito de brilho adicional
    "&::after": {
      content: '""',
      position: "absolute",
      top: 0,
      left: "-100%",
      width: "100%",
      height: "100%",
      background: "linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent)",
      transition: "left 0.6s ease",
      zIndex: 1,
    },
    
    "&:hover": {
      backgroundColor: "transparent",
      color: "#111b21",
      
      "&::before": {
        width: "100%",
      },
      
      "&::after": {
        left: "100%", // Efeito de brilho adicional
      },
      
      "& .menu-icon": {
        transform: "scale(1.15)",
        color: "#00a884",
        animation: "$bounceIcon 0.6s ease",
      },
      
      "& .menu-text": {
        fontWeight: 600,
        transform: "translateX(2px)", // Sutil movimento do texto
      },
    },
    
    "&.dangerous": {
      "&::before": {
        background: "linear-gradient(90deg, rgba(254, 242, 242, 0.4) 0%, rgba(254, 242, 242, 0.8) 100%)",
      },
      
      "&:hover": {
        color: "#e06162",
        
        "& .menu-icon": {
          color: "#e06162",
          animation: "$bounceIcon 0.6s ease",
        },
      },
    },
  },
    // 🎯 Ícones do menu - mais fluidos
  menuIcon: {
    fontSize: "18px",
    color: "#667781",
    marginRight: "18px", // Espaçamento maior
    minWidth: "18px",
    transition: "all 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55)", // Bounce suave
    zIndex: 2,
    position: "relative",
    
    "&.dangerous": {
      color: "#e06162"
    }
  },
  
    // 🎯 Texto do menu - mais refinado
  menuText: {
    fontSize: "14px",
    fontWeight: 400,
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    zIndex: 2,
    position: "relative",
    
    "& .MuiTypography-root": {
      fontSize: "14px",
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      letterSpacing: "0.25px", // Melhor legibilidade
    }
  },
    // 🎯 Divider - mais elegante
  divider: {
    margin: "6px 12px", // Margem lateral para melhor visual
    backgroundColor: "#e9edef",
    opacity: 0.7,
  },
  
  // 🎯 Popover de reações - estilo WhatsApp
  reactionsPopover: {
    "& .MuiPaper-root": {
      borderRadius: "25px",
      boxShadow: "0 2px 8px rgba(11, 20, 26, 0.26)",
      backgroundColor: "#ffffff",
      padding: "6px 8px",
      border: "1px solid rgba(0, 0, 0, 0.05)"
    }
  },
  
  // 🎯 Grid de reações
  reactionsGrid: {
    padding: "2px",
    gap: "2px"
  },
  
  // 🎯 Botões de reação
  reactionButton: {
    width: "36px",
    height: "36px",
    fontSize: "18px",
    borderRadius: "50%",
    transition: "all 0.15s ease",
    
    "&:hover": {
      backgroundColor: "#f0f2f5",
      transform: "scale(1.2)"
    }
  },
    // 🎯 Botão de mais reações
  moreReactionsButton: {
    width: "36px",
    height: "36px",    borderRadius: "50%",
    backgroundColor: "#f0f2f5",
    color: "#667781",
    
    "&:hover": {
      backgroundColor: "#e9edef",
      transform: "scale(1.05)"
    }
  },  // 🎯 Estilos para o popover com EmojiPicker completo
  extraReactionsPopover: {
    "& .MuiPopover-root": {
      // 🐛 Previne o bug de posicionamento
      position: "fixed !important",
      zIndex: "9999 !important",
    },
    
    "& .MuiPaper-root": {
      borderRadius: "16px !important",
      padding: "0 !important", // Sem padding para o EmojiPicker
      width: "400px !important", // Largura otimizada para EmojiPicker
      height: "450px !important", // Altura otimizada para EmojiPicker
      maxWidth: "400px !important",
      maxHeight: "450px !important",
      overflow: "hidden !important", // Sem scroll no Paper
      backgroundColor: "rgba(255, 255, 255, 0.98) !important",
      backdropFilter: "blur(10px) !important",
      border: "1px solid rgba(0, 0, 0, 0.08) !important",
      boxShadow: "0 8px 32px rgba(0, 0, 0, 0.15) !important",
      // 🐛 Força posição estável
      position: "relative !important",
      transform: "none !important",
    }
  },
  
  // 🎯 Container do EmojiPicker personalizado para WhatsApp
  emojiPickerContainer: {
    "& .EmojiPickerReact": {
      border: "none !important",
      borderRadius: "16px !important",
      boxShadow: "none !important",
      backgroundColor: "transparent !important",
      fontFamily: '"Segoe UI Emoji", "Apple Color Emoji", "Noto Color Emoji" !important',
      
      "& .epr-header": {
        borderBottom: "1px solid rgba(0, 0, 0, 0.08) !important",
        padding: "12px 16px !important",
        backgroundColor: "#f8f9fa !important",
      },
      
      "& .epr-search": {
        backgroundColor: "#f0f2f5 !important",
        border: "none !important",
        borderRadius: "20px !important",
        padding: "8px 16px !important",
        margin: "0 16px 12px 16px !important",
        fontSize: "14px !important",
        
        "&:focus": {
          backgroundColor: "#ffffff !important",
          border: "2px solid #00a884 !important",
          outline: "none !important",
        },
        
        "&::placeholder": {
          color: "#667781 !important",
        }
      },
      
      "& .epr-category-nav": {
        padding: "0 12px !important",
        borderBottom: "1px solid rgba(0, 0, 0, 0.08) !important",
        backgroundColor: "#ffffff !important",
        
        "& button": {
          borderRadius: "8px !important",
          margin: "0 2px !important",
          transition: "all 0.2s ease !important",
          
          "&:hover": {
            backgroundColor: "#f0f2f5 !important",
          },
          
          "&.epr-cat-btn.epr-active": {
            backgroundColor: "#00a884 !important",
            color: "white !important",
          }
        }
      },
      
      "& .epr-emoji-category": {
        "& .epr-emoji": {
          borderRadius: "8px !important",
          transition: "all 0.2s ease !important",
          margin: "2px !important",
          
          "&:hover": {
            backgroundColor: "#f0f2f5 !important",
            transform: "scale(1.2) !important",
          }
        }
      },
      
      "& .epr-preview": {
        display: "none !important", // Oculta preview para economizar espaço
      }
    }
  },  
  // 🎯 Estilos mantidos para compatibilidade (podem ser removidos se não usados)
  extraReactionButton: {
    width: "36px",
    height: "36px",
    border: "none",
    borderRadius: "8px",
    backgroundColor: "transparent",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "20px",
    transition: "all 0.2s ease",
    margin: "3px",
    
    "&:hover": {
      backgroundColor: "rgba(0, 0, 0, 0.08)",
      transform: "scale(1.15)",
    },
    
    "&:active": {
      transform: "scale(1.05)",
    }
  }
}));

const FloatingMessageActions = ({
  message,
  isVisible,
  fromMe,
  isGroup,
  whatsappId,
  queueId,
  onReply,
  onForward,
  onReact
}) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const { setReplyingMessage } = useContext(ReplyMessageContext);
  const { setEditingMessage } = useContext(EditMessageContext);
  const { setShowSelectMessageCheckbox } = useContext(ForwardMessageContext);
  const { setTabOpen } = useContext(TicketsContext);
  const history = useHistory();
  // Estados
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [reactionAnchorEl, setReactionAnchorEl] = useState(null);
  const [moreReactionsAnchorEl, setMoreReactionsAnchorEl] = useState(null);
  const [openAlert, setOpenAlert] = useState(false);
  const [userTicketOpen, setUserTicketOpen] = useState("");
  const [queueTicketOpen, setQueueTicketOpen] = useState("");
  const [acceptTicketWithouSelectQueueOpen, setAcceptTicketWithouSelectQueueOpen] = useState(false);
  const [ticketOpen, setTicketOpen] = useState(null);
  // 🇧🇷 Estado para busca traduzida no EmojiPicker
  const [searchValue, setSearchValue] = useState('');  // 🇧🇷 UseEffect para interceptar e traduzir busca do EmojiPicker
  useEffect(() => {
    if (!moreReactionsAnchorEl) return;

    const interceptEmojiSearch = () => {
      // Procura pelo input de busca do EmojiPicker
      const searchInput = document.querySelector('.EmojiPickerReact .epr-search');
      
      if (searchInput && !searchInput.dataset.intercepted) {
        searchInput.dataset.intercepted = 'true';
        
        // Atualiza o placeholder
        searchInput.placeholder = 'Buscar emoji em português...';
        
        let isTranslating = false;
        
        // Intercepta eventos de input
        const handleInput = (e) => {
          if (isTranslating) return; // Evita loop infinito
          
          const originalValue = e.target.value;
          const translatedValue = translateSearchTerm(originalValue);
          
          // Se houve tradução diferente do original
          if (translatedValue !== originalValue && translatedValue && originalValue) {
            isTranslating = true;
            
            // Salva posição do cursor
            const cursorPosition = e.target.selectionStart;
            
            // Aplica a tradução
            e.target.value = translatedValue;
            
            // Dispara eventos para o EmojiPicker processar
            const inputEvent = new Event('input', { bubbles: true });
            e.target.dispatchEvent(inputEvent);
            
            // Restaura posição do cursor (aproximadamente)
            const newPosition = Math.min(cursorPosition, translatedValue.length);
            e.target.setSelectionRange(newPosition, newPosition);
            
            isTranslating = false;
          }
        };
        
        // Event listeners
        searchInput.addEventListener('input', handleInput, true);
        
        // Cleanup quando necessário
        const cleanup = () => {
          searchInput.removeEventListener('input', handleInput, true);
          if (searchInput.dataset.intercepted) {
            delete searchInput.dataset.intercepted;
          }
        };
        
        // Observer para detectar quando o EmojiPicker é removido
        const observer = new MutationObserver(() => {
          const stillExists = document.querySelector('.EmojiPickerReact .epr-search');
          if (!stillExists) {
            cleanup();
            observer.disconnect();
          }
        });
        
        observer.observe(document.body, { childList: true, subtree: true });
        
        // Retorna função de cleanup
        return cleanup;
      }
    };

    // Tenta interceptar múltiplas vezes para garantir sucesso
    let attempts = 0;
    const maxAttempts = 10;
    
    const tryIntercept = () => {
      attempts++;
      const cleanup = interceptEmojiSearch();
      
      if (!cleanup && attempts < maxAttempts) {
        setTimeout(tryIntercept, 50);
      }
      
      return cleanup;
    };
    
    const cleanup = tryIntercept();
    
    return () => {
      if (cleanup) cleanup();
    };
  }, [moreReactionsAnchorEl]);
  // 🐛 UseEffect para prevenir bug do EmojiPicker pular para canto da tela
  useEffect(() => {
    if (!moreReactionsAnchorEl) return;

    // Função para fechar o popover quando a janela perde o foco
    const handleWindowBlur = () => {
      setMoreReactionsAnchorEl(null);
    };    // Função para detectar cliques fora da área do popover
    const handleClickOutside = (event) => {
      const popoverElement = document.querySelector('.MuiPopover-root');
      const emojiPickerElement = document.querySelector('.MuiPopover-root .EmojiPickerReact');
      
      if (popoverElement && emojiPickerElement) {
        // Lista de seletores de elementos internos do EmojiPicker que devem manter o popover aberto
        const internalElements = [
          '.epr-search',           // Campo de busca
          '.epr-search-container', // Container do campo de busca
          '.epr-header',           // Cabeçalho
          '.epr-category-nav',     // Navegação de categorias
          '.epr-emoji-category',   // Categorias de emoji
          '.epr-emoji',            // Emojis individuais
          '.epr-body',             // Corpo do picker
          '.epr-preview'           // Preview (se habilitado)
        ];
        
        // Verifica se o clique foi em qualquer elemento interno
        let isClickInsideInternal = false;
        for (const selector of internalElements) {
          const element = document.querySelector(`.MuiPopover-root ${selector}`);
          if (element && element.contains(event.target)) {
            isClickInsideInternal = true;
            break;
          }
        }
        
        // Verificações principais
        const isClickInsidePopover = popoverElement.contains(event.target);
        const isClickInsideEmojiPicker = emojiPickerElement.contains(event.target);
        
        // ✅ SÓ fecha se o clique for COMPLETAMENTE fora de todos os elementos
        if (!isClickInsidePopover && !isClickInsideEmojiPicker && !isClickInsideInternal) {
          setMoreReactionsAnchorEl(null);
        }
      } else if (popoverElement && !popoverElement.contains(event.target)) {
        // Fallback: se não encontrou o EmojiPicker, usa apenas o popover
        setMoreReactionsAnchorEl(null);
      }
    };

    // Função para detectar quando a janela é redimensionada
    const handleWindowResize = () => {
      setMoreReactionsAnchorEl(null);
    };    // 🐛 Função para detectar quando o cursor sai da tela (outros monitores)
    const handleMouseLeave = (event) => {
      // Verifica se realmente saiu da área visível da janela
      const rect = document.body.getBoundingClientRect();
      const { clientX, clientY } = event;
      
      // Se o cursor saiu completamente da área visível
      if (clientX < rect.left || clientX > rect.right || 
          clientY < rect.top || clientY > rect.bottom) {
        // ⚠️ Fecha o popover ao invés de deixar ele "vagar" pela tela
        setMoreReactionsAnchorEl(null);
      }
    };    // 🐛 Função específica para scroll - apenas fecha se scroll for FORA do EmojiPicker
    const handleScroll = (event) => {
      const emojiPickerElement = document.querySelector('.MuiPopover-root .EmojiPickerReact');
      const popoverElement = document.querySelector('.MuiPopover-root');
      
      // 🚨 IMPORTANTE: Permitir scroll dentro da lista de emojis
      if (emojiPickerElement && popoverElement) {
        // Verifica se o elemento que disparou o scroll está dentro do EmojiPicker
        const scrollTarget = event.target;
        const isScrollInsideEmojiPicker = emojiPickerElement.contains(scrollTarget);
        const isScrollInsidePopover = popoverElement.contains(scrollTarget);
        
        // ✅ Só fecha se o scroll for de um elemento COMPLETAMENTE fora do componente
        // ❌ NÃO fecha se for scroll da lista de emojis ou qualquer parte do popover
        if (!isScrollInsideEmojiPicker && !isScrollInsidePopover) {
          setMoreReactionsAnchorEl(null);
        }
      } else {
        // Se não conseguiu encontrar os elementos, fecha por segurança
        setMoreReactionsAnchorEl(null);
      }
    };    // Adiciona os event listeners
    window.addEventListener('blur', handleWindowBlur);
    window.addEventListener('resize', handleWindowResize);
    document.addEventListener('mouseleave', handleMouseLeave);
    document.addEventListener('mousedown', handleClickOutside);
    
    // 🐛 Scroll listener mais específico - apenas em elementos fora do EmojiPicker
    document.addEventListener('scroll', handleScroll, true);

    // 🚨 Listener adicional para detectar se o popover "sumiu" da tela
    const checkPopoverPosition = () => {
      const popoverElement = document.querySelector('.MuiPopover-root');
      if (popoverElement) {
        const rect = popoverElement.getBoundingClientRect();
        // Se o popover está completamente fora da viewport
        if (rect.right < 0 || rect.left > window.innerWidth || 
            rect.bottom < 0 || rect.top > window.innerHeight) {
          setMoreReactionsAnchorEl(null);
        }
      }
    };

    // Verifica posição periodicamente quando o popover está aberto
    const positionCheckInterval = setInterval(checkPopoverPosition, 500);    // Cleanup dos event listeners
    return () => {
      window.removeEventListener('blur', handleWindowBlur);
      window.removeEventListener('resize', handleWindowResize);
      document.removeEventListener('mouseleave', handleMouseLeave);
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('scroll', handleScroll, true);
      clearInterval(positionCheckInterval);
    };
  }, [moreReactionsAnchorEl]);

  // 🇧🇷 Função para traduzir termos de busca
  const translateSearchTerm = (searchTerm) => {
    if (!searchTerm) return '';
    
    const lowerTerm = searchTerm.toLowerCase().trim();
    
    // Verifica se há tradução direta
    if (emojiTranslations[lowerTerm]) {
      return emojiTranslations[lowerTerm];
    }
    
    // Busca por palavras parciais
    const partialMatches = Object.keys(emojiTranslations).filter(key => 
      key.includes(lowerTerm) || lowerTerm.includes(key)
    );
    
    if (partialMatches.length > 0) {
      return emojiTranslations[partialMatches[0]];
    }
    
    // Se não encontrou tradução, retorna o termo original
    return searchTerm;
  };// Reações rápidas (igual WhatsApp) - os 6 principais
  const quickReactions = ["👍", "❤️", "😂", "😮", "😢", "🙏"];
  
  // Reações extras para o modal expandido
  const extraReactions = [
    "😀", "😃", "😄", "😁", "😆", "😅", "🤣", "😂", "🙂", "🙃",
    "😉", "😊", "😇", "🥰", "😍", "🤩", "😘", "😗", "😚", "😙",
    "😋", "😛", "😜", "🤪", "😝", "🤑", "🤗", "🤭", "🤫", "🤔",
    "🤐", "🤨", "😐", "😑", "😶", "😏", "😒", "🙄", "😬", "🤥",
    "😔", "😕", "🙁", "☹️", "😣", "😖", "😫", "😩", "🥺", "😢",
    "😭", "😤", "😠", "😡", "🤬", "🤯", "😳", "🥵", "🥶", "😱",
    "😨", "😰", "😥", "😓", "🤗", "🤔", "🤭", "🤫", "🤥", "😶",
    "😐", "😑", "😬", "🙄", "😯", "😦", "😧", "😮", "😲", "🥱",
    "😴", "🤤", "😪", "😵", "🤐", "🥴", "🤢", "🤮", "🤧", "😷",
    "🤒", "🤕", "🤑", "🤠", "😈", "👿", "👹", "👺", "🤡", "💩",
    "👻", "💀", "☠️", "👽", "👾", "🤖", "🎃", "😺", "😸", "😹",
    "😻", "😼", "😽", "🙀", "😿", "😾", "🤲", "👐", "🙌", "👏",
    "🤝", "👍", "👎", "👊", "✊", "🤛", "🤜", "🤞", "✌️", "🤟",
    "🤘", "👌", "🤏", "👈", "👉", "👆", "👇", "☝️", "✋", "🤚",
    "🖐", "🖖", "👋", "🤙", "💪", "🦾", "🖕", "✍️", "🙏", "🦶",
    "🦵", "🦿", "💄", "💋", "👄", "🦷", "👅", "👂", "🦻", "👃",
    "👣", "👁", "👀", "🧠", "🗣", "👤", "👥", "👶", "👧", "🧒",
    "👦", "👩", "🧑", "👨", "👱‍♀️", "👱", "👱‍♂️", "🧔", "👵", "🧓",
    "👴", "👲", "👳‍♀️", "👳", "👳‍♂️", "🧕", "👮‍♀️", "👮", "👮‍♂️", "👷‍♀️"
  ];
  
  // 🇧🇷 Dicionário de tradução Português → Inglês para busca de emojis
  const emojiTranslations = {
    // Sentimentos e expressões
    "feliz": "happy smile joy",
    "triste": "sad cry tears",
    "coração": "heart love",
    "amor": "love heart kiss",
    "raiva": "angry mad rage",
    "bravo": "angry mad",
    "rindo": "laugh laughing funny",
    "chorando": "cry crying tears",
    "surpreso": "surprised shock wow",
    "assustado": "scared fear afraid",
    "cansado": "tired sleepy",
    "dormindo": "sleep sleeping tired",
    "pensando": "thinking think",
    "confuso": "confused",
    "zangado": "angry mad",
    "apaixonado": "love heart eyes",
    "beijando": "kiss kissing",
    "piscando": "wink winking",
    
    // Animais
    "gato": "cat kitty",
    "cachorro": "dog puppy",
    "passaro": "bird",
    "pássaro": "bird",
    "peixe": "fish",
    "cobra": "snake",
    "rato": "mouse rat",
    "urso": "bear",
    "leão": "lion",
    "tigre": "tiger",
    "elefante": "elephant",
    "macaco": "monkey",
    "vaca": "cow",
    "porco": "pig",
    "ovelha": "sheep",
    "cavalo": "horse",
    "galinha": "chicken",
    "pato": "duck",
    
    // Comida
    "comida": "food eat",
    "pizza": "pizza",
    "hamburguer": "burger hamburger",
    "batata": "potato",
    "maçã": "apple",
    "banana": "banana",
    "laranja": "orange",
    "uva": "grape grapes",
    "morango": "strawberry",
    "abacaxi": "pineapple",
    "melancia": "watermelon",
    "cereja": "cherry",
    "pêssego": "peach",
    "limão": "lemon",
    "café": "coffee",
    "cerveja": "beer",
    "vinho": "wine",
    "água": "water",
    "leite": "milk",
    "pão": "bread",
    "bolo": "cake",
    "doce": "candy sweet",
    "sorvete": "ice cream",
    
    // Objetos
    "telefone": "phone telephone",
    "computador": "computer laptop",
    "carro": "car automobile",
    "casa": "house home",
    "livro": "book",
    "relógio": "watch clock time",
    "dinheiro": "money dollar",
    "presente": "gift present",
    "festa": "party celebration",
    "música": "music note",
    "futebol": "soccer football",
    "basquete": "basketball",
    "bola": "ball",
    "jogo": "game play",
    
    // Natureza
    "sol": "sun sunny",
    "lua": "moon",
    "estrela": "star",
    "nuvem": "cloud cloudy",
    "chuva": "rain rainy",
    "neve": "snow snowy",
    "fogo": "fire flame",
    "água": "water",
    "árvore": "tree",
    "flor": "flower",
    "folha": "leaf",
    "praia": "beach",
    "montanha": "mountain",
    
    // Símbolos
    "ok": "ok okay thumb",
    "legal": "cool awesome",
    "obrigado": "thanks thank you",
    "sim": "yes check",
    "não": "no cross",
    "pare": "stop",    "atenção": "warning attention",
    "perigo": "danger warning",
    
    // Gírias e expressões brasileiras
    "top": "cool awesome great",
    "massa": "cool awesome great",
    "show": "cool awesome great",
    "demais": "awesome amazing",
    "incrível": "amazing incredible",
    "perfeito": "perfect excellent",
    "máximo": "awesome great",
    "sensacional": "amazing awesome",
    "fantástico": "fantastic amazing",
    "maravilhoso": "wonderful amazing",
    "ótimo": "great excellent",
    "bom": "good great",
    "ruim": "bad terrible",
    "péssimo": "terrible awful",
    "horrível": "horrible awful",
    "nojento": "disgusting gross",
    "que nojo": "disgusting gross",
    "eca": "disgusting gross",
    "eita": "wow surprised",
    "nossa": "wow surprised",
    "caramba": "wow surprised",
    "putz": "wow surprised",
    "poxa": "disappointed",
    "droga": "annoyed angry",
    "caraca": "wow surprised",
    "mano": "dude bro",
    "cara": "dude guy",
    "gente": "people guys",
    "galera": "people friends",
    "pessoal": "people everyone",
    "turma": "group friends",
    "time": "team group",
    "família": "family",
    "amigos": "friends",
    "namorado": "boyfriend love",
    "namorada": "girlfriend love",
    "mozão": "love honey",
    "benzinho": "love honey",
    "querido": "dear love",
    "flor": "flower dear",
    "lindinho": "cute beautiful",
    "gatinho": "cute cat",
    "fofinho": "cute adorable",
    "bebê": "baby cute",
    "criança": "child baby",
    "vovô": "grandfather old",
    "vovó": "grandmother old",
    "titio": "uncle",
    "titia": "aunt",
    "mamãe": "mother mom",
    "papai": "father dad",
    "irmão": "brother",
    "irmã": "sister",
    "primo": "cousin",
    "prima": "cousin",
    
    // Ações e atividades
    "dormindo": "sleeping sleep",
    "acordando": "wake waking",
    "trabalhando": "working work",
    "estudando": "studying study",
    "lendo": "reading book",
    "escrevendo": "writing",
    "correndo": "running run",
    "caminhando": "walking walk",
    "nadando": "swimming swim",
    "dançando": "dancing dance",
    "cantando": "singing sing",
    "tocando": "playing music",
    "cozinhando": "cooking cook",
    "comendo": "eating food",
    "bebendo": "drinking drink",
    "viajando": "traveling travel",
    "dirigindo": "driving car",
    "voando": "flying plane",
    "chegando": "arriving come",
    "saindo": "leaving go",
    "voltando": "returning back",
    "esperando": "waiting wait",
    "procurando": "searching look",
    "encontrando": "finding found",
    "perdendo": "losing lost",
    "ganhando": "winning win",
    "jogando": "playing game",
    "assistindo": "watching tv",
    "ouvindo": "listening music",
    "falando": "talking speak",
    "gritando": "shouting scream",
    "sussurrando": "whispering quiet",
    "silêncio": "quiet silence",
    "barulho": "noise loud",
    "alto": "loud high",
    "baixo": "quiet low",
    "rápido": "fast quick",
    "devagar": "slow",
    "longe": "far away",
    "perto": "near close",
    "aqui": "here",
    "lá": "there",
    "agora": "now",
    "depois": "later after",
    "antes": "before",
    "sempre": "always",
    "nunca": "never",
    "talvez": "maybe perhaps",
    
    // Cores
    "colorido": "colorful rainbow",
    "arco-íris": "rainbow",
    
    // Países e locais
    "brasil": "brazil flag",
    "américa": "america flag",
    "europa": "europe",
    "ásia": "asia",
    "áfrica": "africa",
    "mundo": "world earth",
    "terra": "earth world",
    "planeta": "planet earth",
    "espaço": "space rocket",
    "lua": "moon space",
    "estrelas": "stars space",
    "universo": "universe space",
    
    // Tecnologia
    "internet": "computer web",
    "site": "computer web",
    "app": "phone mobile",
    "celular": "phone mobile",
    "smartphone": "phone mobile",
    "tablet": "computer mobile",
    "notebook": "computer laptop",
    "desktop": "computer",
    "teclado": "keyboard computer",
    "mouse": "computer",
    "tela": "screen computer",
    "wifi": "signal internet",
    "bluetooth": "signal connection",
    "bateria": "battery power",
    "carregador": "battery power",
    "cabo": "cable wire",
    "fone": "headphones music",
    "alto-falante": "speaker music",
    "microfone": "microphone",
    "câmera": "camera photo",
    "foto": "camera photo",
    "vídeo": "video camera",
    "filme": "movie video",
    "série": "tv movie",
    "desenho": "cartoon",
    "anime": "cartoon japan",
    
    // Esportes
    "corrida": "running run",
    "maratona": "running marathon",
    "natação": "swimming swim",
    "ginástica": "gymnastics exercise",
    "academia": "gym exercise",
    "musculação": "muscle gym",
    "yoga": "yoga exercise",
    "pilates": "exercise",
    "caminhada": "walking walk",
    "ciclismo": "bicycle bike",
    "bicicleta": "bicycle bike",
    "skate": "skateboard",
    "patins": "roller skate",
    "tênis": "tennis sport",
    "vôlei": "volleyball sport",
    "handebol": "handball sport",
    "rugby": "rugby sport",
    "golfe": "golf sport",
    "surf": "surfing wave",
    "esqui": "skiing snow",
    "snowboard": "snowboard snow",
    
    // Tempo e clima
    "calor": "hot sun",
    "frio": "cold snow",
    "quente": "hot fire",
    "gelado": "cold ice",
    "ventando": "wind windy",
    "tempestade": "storm thunder",
    "trovão": "thunder storm",
    "raio": "lightning storm",
    "neblina": "fog cloudy",
    "garoa": "rain drizzle",
    "temporal": "storm rain",
    "seca": "desert dry",
    "umidade": "humid wet",
    
    // Transportes
    "ônibus": "bus",
    "trem": "train",
    "metrô": "subway train",
    "taxi": "taxi car",
    "uber": "car taxi",
    "bicicleta": "bicycle bike",
    "moto": "motorcycle",
    "barco": "boat ship",
    "navio": "ship boat",
    "avião": "airplane plane",
    "helicóptero": "helicopter",
    "foguete": "rocket space",
    
    // Saúde
    "médico": "doctor health",
    "hospital": "hospital health",
    "remédio": "medicine pill",
    "vacina": "vaccine medicine",
    "doente": "sick ill",
    "saudável": "healthy",
    "forte": "strong muscle",
    "fraco": "weak",
    "dor": "pain hurt",
    "machucado": "hurt injured",
    "curado": "healed healthy",
    "vitamina": "vitamin health",
    "exercício": "exercise gym",
    "proibido": "forbidden prohibited",
    "paz": "peace",
    "vitória": "victory peace",
    
    // Cores
    "vermelho": "red",
    "azul": "blue",
    "verde": "green",
    "amarelo": "yellow",
    "roxo": "purple",
    "rosa": "pink",
    "preto": "black",
    "branco": "white",
    "laranja": "orange",
    
    // Ações
    "correndo": "running run",
    "caminhando": "walking walk",
    "pulando": "jumping jump",
    "dançando": "dancing dance",
    "cantando": "singing sing",
    "trabalhando": "working work",
    "estudando": "studying study",
    "lendo": "reading read",
    "escrevendo": "writing write",
    "cozinhando": "cooking cook"
  };
  
  // 🎯 Feedback háptico/sonoro melhorado
  const provideFeedback = (type = "default") => {
    // Vibração háptica (mobile)
    if (navigator.vibrate) {
      const patterns = {
        default: [50],
        success: [50, 50, 50],
        warning: [100],
        danger: [200, 100, 200]
      };
      navigator.vibrate(patterns[type] || patterns.default);
    }
    
    // Feedback sonoro sutil (opcional)
    // Pode ser implementado com Web Audio API se desejado
  };
    // Handlers principais - com feedback melhorado
  const handleReply = () => {
    provideFeedback("default");
    setReplyingMessage(message);
    if (onReply) onReply(message);
  };
  
  const handleForward = () => {
    provideFeedback("success");
    setShowSelectMessageCheckbox(true);
    if (onForward) onForward(message);
  };
  
  const handleReact = (emoji) => {
    provideFeedback("default");
    if (onReact) onReact(message, emoji);
    setReactionAnchorEl(null);
    setMoreReactionsAnchorEl(null);
  };
  
  const handleEdit = () => {
    provideFeedback("default");
    setEditingMessage(message);
    setMenuAnchorEl(null);
  };
  
  const handleDelete = async () => {
    provideFeedback("danger");
    try {
      await api.delete(`/messages/${message.id}`);
      toast.success("Mensagem excluída");
    } catch (err) {
      toastError(err);
    }
    setMenuAnchorEl(null);
  };

  // Baixar áudio (para mensagens de áudio)
  const handleDownloadAudio = async () => {
    try {
      const url = String(message?.mediaUrl || "");
      if (!url) {
        toast.error("URL de áudio indisponível");
        return;
      }

      const normalizeMediaUrl = (u) => {
        let out = u;
        try {
          if (out.includes("localhost:443/")) {
            out = out.replace("localhost:443/", "localhost:8080/");
          }
          const loc = window.location;
          const isHttpsPage = loc.protocol === "https:";
          const urlObj = new URL(out, loc.origin);
          if (out.startsWith("/public/")) {
            urlObj.protocol = loc.protocol;
            urlObj.host = loc.host;
          }
          if (isHttpsPage && urlObj.protocol === "http:") {
            urlObj.protocol = "https:";
          }
          return urlObj.toString();
        } catch {
          return out;
        }
      };

      const href = normalizeMediaUrl(url);

      // Tenta baixar como Blob para forçar download
      let blob;
      try {
        const res = await fetch(href, { credentials: 'include' });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        blob = await res.blob();
      } catch (e) {
        // Fallback: liga direto para a URL com atributo download
        const a = document.createElement("a");
        a.href = href;
        a.setAttribute("download", message?.filename || message?.originalName || `audio-${message?.id || "mensagem"}`);
        a.rel = "noopener";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        return;
      }

      const guessedFromUrl = (() => {
        try {
          const u = new URL(href);
          const pathname = u.pathname || "";
          const base = pathname.split("/").pop() || "";
          return base || null;
        } catch { return null; }
      })();
      const mime = (blob && blob.type) || '';
      const extFromMime = mime.includes('ogg') ? '.ogg' : (mime.includes('mpeg') ? '.mp3' : (mime.includes('mp4') ? '.m4a' : ''));
      let baseName = message?.filename || message?.originalName || guessedFromUrl || `audio-${message?.id || "mensagem"}`;
      if (extFromMime && !/\.[a-z0-9]{2,5}$/i.test(baseName)) {
        baseName += extFromMime;
      }

      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.setAttribute("download", baseName);
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(objectUrl), 5000);
    } catch (e) {
      console.error("Falha ao baixar áudio:", e);
      toastError(e);
    } finally {
      setMenuAnchorEl(null);
    }
  };
  
  // Verificar se pode editar (15 minutos)
  const canEdit = () => {
    const fifteenMinutesInMs = 15 * 60 * 1000;
    const currentTime = new Date();
    const messageTime = new Date(message.createdAt);
    return currentTime - messageTime <= fifteenMinutesInMs;
  };
  
  // Abrir menu de reações
  const openReactionsMenu = (event) => {
    setReactionAnchorEl(event.currentTarget);
  };
    const openMoreReactions = (event) => {
    setMoreReactionsAnchorEl(event.currentTarget);
    setReactionAnchorEl(null); // Fecha o primeiro popover
  };
  
  const closeMoreReactions = () => {
    setMoreReactionsAnchorEl(null);
  };

  // Conversar com (apenas grupos / mensagens não minhas)
  const handleTalkTo = async (contactId) => {
    try {
      const { data: ticket } = await api.post("/tickets", {
        contactId,
        userId: user?.id,
        status: "open",
        queueId,
        whatsappId,
      });

      setTicketOpen(ticket);
      if (ticket.queueId === null) {
        setAcceptTicketWithouSelectQueueOpen(true);
      } else {
        setTabOpen("open");
        history.push(`/tickets/${ticket.uuid}`);
      }
    } catch (err) {
      try {
        const ticket = JSON.parse(err?.response?.data?.error);
        if (ticket.userId !== user?.id) {
          setOpenAlert(true);
          setUserTicketOpen(ticket?.user?.name);
          setQueueTicketOpen(ticket?.queue?.name);
        } else {
          setOpenAlert(false);
          setUserTicketOpen("");
          setQueueTicketOpen("");
          setTabOpen(ticket.status);
          history.push(`/tickets/${ticket.uuid}`);
        }
      } catch (e) {
        toastError(err);
      }
    } finally {
      setMenuAnchorEl(null);
    }
  };

  const handleCloseAlert = () => {
    setOpenAlert(false);
    setUserTicketOpen("");
    setQueueTicketOpen("");
  };
  
  return (
    <>
      <AcceptTicketWithoutQueue
        modalOpen={acceptTicketWithouSelectQueueOpen}
        onClose={() => setAcceptTicketWithouSelectQueueOpen(false)}
        ticket={ticketOpen}
        ticketId={ticketOpen?.id}
      />
      <ShowTicketOpen
        isOpen={openAlert}
        handleClose={handleCloseAlert}
        user={userTicketOpen}
        queue={queueTicketOpen}
      />
      {/* Container dos botões flutuantes */}
      <div 
        className={`${classes.floatingActionsContainer} ${
          fromMe ? 'right' : 'left'
        } ${isVisible ? 'visible' : ''}`}
        data-message-id={message.id}
      >{/* Responder - sempre visível */}
        <MessageActionButton
          variant="default"
          tooltip="Responder"
          onClick={handleReply}
          animate={true}
        >
          <ReplyIcon />        </MessageActionButton>
        {/* Reagir - sempre visível */}
        <MessageActionButton
          variant="default"
          tooltip="Reagir"
          onClick={openReactionsMenu}
          animate={true}
        >
          <EmojiIcon />
        </MessageActionButton>
        {/* Menu mais opções */}
        <MessageActionButton
          variant="default"
          tooltip="Mais opções"
          onClick={(e) => setMenuAnchorEl(e.currentTarget)}
          animate={true}
          data-action="more-options"
        >
          <MoreIcon />
        </MessageActionButton>
      </div>
      
      {/* Menu de opções */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={() => setMenuAnchorEl(null)}
        className={classes.modernMenu}
        getContentAnchorEl={null}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: fromMe ? "left" : "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: fromMe ? "left" : "right",
        }}
      >
        <MenuItem className={classes.menuItem} onClick={handleForward}>
          <ListItemIcon className="menu-icon">
            <ForwardIcon />
          </ListItemIcon>
          <ListItemText primary="Encaminhar" className="menu-text" />
        </MenuItem>

        {/* Conversar com - visível apenas em grupos e mensagens não minhas */}
        {!fromMe && isGroup && message?.contact?.id && (
          <MenuItem className={classes.menuItem} onClick={() => handleTalkTo(message?.contact?.id)}>
            <ListItemIcon className="menu-icon">
              <PersonIcon />
            </ListItemIcon>
            <ListItemText primary={i18n.t("messageOptionsMenu.talkTo") || "Conversar Com"} className="menu-text" />
          </MenuItem>
        )}
        
        <MenuItem className={classes.menuItem}>
          <ListItemIcon className="menu-icon">
            <StarIcon />
          </ListItemIcon>
          <ListItemText primary="Destacar" className="menu-text" />
        </MenuItem>
        
        <MenuItem className={classes.menuItem}>
          <ListItemIcon className="menu-icon">
            <CopyIcon />
          </ListItemIcon>
          <ListItemText primary="Copiar" className="menu-text" />
        </MenuItem>

        {/* Opção para baixar áudio (qualquer direção) */}
        {message.mediaType === "audio" && ([
          <Divider key="audio-download-divider" className={classes.divider} />,
          <MenuItem key="audio-download" className={classes.menuItem} onClick={handleDownloadAudio}>
            <ListItemIcon className="menu-icon">
              <DownloadIcon />
            </ListItemIcon>
            <ListItemText primary="Baixar áudio" className="menu-text" />
          </MenuItem>
        ])}
        
        {/* Opções apenas para mensagens próprias */}
        {fromMe && ([
          <Divider key="owner-divider" className={classes.divider} />,
          canEdit() && (
            <MenuItem key="owner-edit" className={classes.menuItem} onClick={handleEdit}>
              <ListItemIcon className="menu-icon">
                <EditIcon />
              </ListItemIcon>
              <ListItemText primary="Editar" className="menu-text" />
            </MenuItem>
          ),
          <MenuItem
            key="owner-delete"
            className={`${classes.menuItem} dangerous`}
            onClick={handleDelete}
          >
            <ListItemIcon className="menu-icon dangerous">
              <DeleteIcon />
            </ListItemIcon>
            <ListItemText primary="Excluir" className="menu-text" />
          </MenuItem>
        ])}
        
        {/* Transcrever áudio */}
        {message.mediaType === "audio" && !fromMe && ([
          <Divider key="audio-divider" className={classes.divider} />,
          <MenuItem key="audio-transcribe" className={classes.menuItem}>
            <ListItemIcon className="menu-icon">
              <TranscribeIcon />
            </ListItemIcon>
            <ListItemText primary="Transcrever" className="menu-text" />
          </MenuItem>
        ])}
      </Menu>
      {/* AnimatedReactionPicker - Seletor de reações animado */}
      <AnimatedReactionPicker
        anchorEl={reactionAnchorEl}
        open={Boolean(reactionAnchorEl)}
        onClose={() => setReactionAnchorEl(null)}
        onReactionSelect={handleReact}
        onMoreReactions={openMoreReactions}
        reactions={quickReactions}
        placement="top"
        animate={true}
      />
      {/* Popover com EmojiPicker completo - 3000+ emojis do WhatsApp */}
      <Popover
        open={Boolean(moreReactionsAnchorEl)}
        anchorEl={moreReactionsAnchorEl}
        onClose={closeMoreReactions}
        className={classes.extraReactionsPopover}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        TransitionComponent={Fade}
        transitionDuration={300}
        // 🐛 Configurações para prevenir bug de posicionamento
        disableAutoFocus={true}
        disableEnforceFocus={true}
        disableRestoreFocus={true}
        keepMounted={false}
        // Configuração de portal para manter referência
        container={() => document.body}
        // Configuração adicional de posicionamento
        marginThreshold={16}
        // Prevent scroll quando aberto
        disableScrollLock={false}
      ><div className={classes.emojiPickerContainer}>
          <EmojiPicker
            onEmojiClick={(emojiObject) => {
              handleReact(emojiObject.emoji);
              closeMoreReactions();
            }}
            width={400}
            height={450}
            previewConfig={{ 
              showPreview: false 
            }}
            searchDisabled={false}
            skinTonesDisabled={true}
            autoFocusSearch={false}
            lazyLoadEmojis={true}
            theme="light"
            emojiStyle="native"
            searchPlaceholder="Buscar emoji em português..."
          />
        </div>
      </Popover>
    </>
  );
};

export default FloatingMessageActions;
